import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
export var context = createContext({
  icon: 'chevron',
  size: 'm'
});
export var openedContext = createContext(false);
export function useCollapseItem() {
  var opened = useContext(openedContext);
  return {
    opened: opened
  };
}
export function useAccordion(detailsRef) {
  var duration = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 400;
  var stateRef = useRef('idle');
  var summaryRef = useRef();
  var contentRef = useRef();
  var animationRef = useRef(null);
  var animationConfig = {
    duration: duration,
    easing: 'cubic-bezier(0.24, 0.1, 0.24, 1)'
  };
  var onFinish = useCallback(function (openState) {
    if (!detailsRef.current) {
      return;
    }
    var el = detailsRef.current;
    el.open = openState;
    animationRef.current = null;
    stateRef.current = 'idle';
    el.style.height = el.style.overflow = '';
    delete el.dataset.animating;
  }, [detailsRef]);
  var cancelAnimation = useCallback(function () {
    stateRef.current = 'idle';
  }, []);
  var completion = useMemo(function () {
    return {
      open: onFinish.bind(null, true),
      close: onFinish.bind(null, false)
    };
  }, [onFinish]);
  var expand = useCallback(function () {
    var _a;
    if (!detailsRef.current || !summaryRef.current || !contentRef.current) {
      return;
    }
    var el = detailsRef.current;
    var sum = summaryRef.current;
    var content = contentRef.current;
    stateRef.current = 'expanding';
    var styleEl = window.getComputedStyle(el);
    var padding = parseInt(styleEl.padding);
    var marginTop = parseInt(window.getComputedStyle(content).marginTop);
    var border = parseInt(styleEl.borderTopWidth) + parseInt(styleEl.borderBottomWidth);
    var startHeight = "".concat(sum.offsetHeight + padding * 2 + border, "px");
    var endHeight = "".concat(sum.offsetHeight + content.offsetHeight + marginTop + padding * 2 + border, "px");
    (_a = animationRef.current) === null || _a === void 0 ? void 0 : _a.cancel();
    animationRef.current = el.animate({
      height: [startHeight, endHeight]
    }, animationConfig);
    animationRef.current.onfinish = completion.open;
    animationRef.current.oncancel = cancelAnimation;
  }, [detailsRef, animationConfig, cancelAnimation, completion]);
  var open = useCallback(function () {
    if (!detailsRef.current) {
      return;
    }
    var el = detailsRef.current;
    el.style.height = "".concat(el.offsetHeight, "px");
    el.open = true;
    window.requestAnimationFrame(expand);
  }, [detailsRef, expand]);
  var shrink = useCallback(function () {
    var _a;
    if (!detailsRef.current || !summaryRef.current || !contentRef.current) {
      return;
    }
    var el = detailsRef.current;
    var sum = summaryRef.current;
    var content = contentRef.current;
    stateRef.current = 'closing';
    var styleEl = window.getComputedStyle(el);
    var padding = parseInt(styleEl.padding);
    var marginTop = parseInt(window.getComputedStyle(content).marginTop);
    var border = parseInt(styleEl.borderTopWidth) + parseInt(styleEl.borderBottomWidth);
    var startHeight = "".concat(sum.offsetHeight + content.offsetHeight + marginTop + padding * 2 + border, "px");
    var endHeight = "".concat(sum.offsetHeight + padding * 2 + border, "px");
    (_a = animationRef.current) === null || _a === void 0 ? void 0 : _a.cancel();
    animationRef.current = el.animate({
      height: [startHeight, endHeight]
    }, animationConfig);
    animationRef.current.onfinish = completion.close;
    animationRef.current.oncancel = cancelAnimation;
  }, [detailsRef, animationConfig, cancelAnimation, completion]);
  var toggle = useCallback(function () {
    if (!detailsRef.current || stateRef.current !== 'idle') {
      return;
    }
    var el = detailsRef.current;
    if (!('animate' in el)) {
      el.open = !el.open;
      return;
    }
    el.style.overflow = 'hidden';
    el.dataset.animating = '';
    if (el.open) {
      shrink();
    } else {
      open();
    }
  }, [detailsRef, open, shrink]);
  useEffect(function () {
    if (!detailsRef.current) {
      return;
    }
    var el = detailsRef.current;
    summaryRef.current = el.querySelector('summary');
    contentRef.current = el.querySelector('[data-content]');
  }, [detailsRef]);
  return {
    toggle: toggle,
    stateRef: stateRef
  };
}